// @flow
import * as React from 'react';
import { Provider } from 'react-redux';
import Context from 'app/common/react/components/Context/Context';
import DebugController from 'app/packages/debugger/DebugController';
import DenzelUserAgent from 'app/common/react/components/DenzelUserAgent/DenzelUserAgent';
import ErrorBoundary from 'app/packages/error-boundary';
import ExperimentsClient from 'app/common/lib/ExperimentsClient';
import ExperimentsProvider from 'app/common/react/components/ExperimentsProvider/ExperimentsProvider';
import FlyoutProvider from 'app/common/react/ui/FlyoutProvider';
import LegacyModalProvider from 'app/common/react/ui/deprecated/LegacyModalProvider';
import OfflineManager from 'app/common/react/ui/OfflineManager';
import ToastManager from 'app/common/react/ui/ToastManager';
import type { ClientContext } from 'app/common/lib/context';
import type { Store } from 'redux';
import UnauthContext from 'app/common/react/components/growth/unauth/UnauthContext';

type Props = {|
  children?: React.Node,
  context: ClientContext,
  // $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
  store: Store,
  experiments: ExperimentsClient,
  seoExperiments: ExperimentsClient,
  seoUnauthExperiments: ExperimentsClient,
|};

export default function App({
  children,
  context,
  experiments,
  seoExperiments,
  seoUnauthExperiments,
  store,
}: Props) {
  if (context.is_authenticated) {
    return (
      <ErrorBoundary name="AuthRoot">
        <Context
          context={context}
          experiments={experiments}
          seoExperiments={seoExperiments}
          seoUnauthExperiments={seoUnauthExperiments}
        >
          <Provider store={store}>
            <DenzelUserAgent context={context}>
              <ExperimentsProvider
                experiments={experiments}
                seoExperiments={seoExperiments}
                seoUnauthExperiments={seoUnauthExperiments}
              >
                <ToastManager siteType={context.site_type}>
                  <LegacyModalProvider>
                    {/*TODO @kaanon remove FlyoutProvider after converting
                        legacy dashboard pages (already in App.js)*/}
                    <FlyoutProvider>
                      <DebugController
                        experiments={experiments}
                        canEnableDebugger={context.user.is_employee}
                      >
                        {children}
                      </DebugController>
                      <OfflineManager />
                    </FlyoutProvider>
                  </LegacyModalProvider>
                </ToastManager>
              </ExperimentsProvider>
            </DenzelUserAgent>
          </Provider>
        </Context>
      </ErrorBoundary>
    );
  } else {
    // Please update webapp/app/common/react/components/UnauthApp.js as well if are making changes to the follow section
    return (
      <ErrorBoundary name="UnauthRoot">
        <UnauthContext
          context={context}
          experiments={experiments}
          seoExperiments={seoExperiments}
          seoUnauthExperiments={seoUnauthExperiments}
        >
          <Provider store={store}>
            <DenzelUserAgent context={context}>
              <ExperimentsProvider
                experiments={experiments}
                seoExperiments={seoExperiments}
                seoUnauthExperiments={seoUnauthExperiments}
              >
                <LegacyModalProvider>{children}</LegacyModalProvider>
              </ExperimentsProvider>
            </DenzelUserAgent>
          </Provider>
        </UnauthContext>
      </ErrorBoundary>
    );
  }
}
